import {router, usePage} from "@inertiajs/vue3";
import { notifyBugsnag } from '@/utils/bugsnag'

// The last user id we asked the SDK to identify, as a string.
//
// The SDK has its own "already identified" guard, but it only sets that flag inside the SUCCESS
// callback of the request. Several events firing before the first response lands — which is
// exactly what a page with per-tile impression tracking does — therefore each start their own
// identify call. This guard is set synchronously, so the burst becomes one request.
let identifiedUserId = null

// init() is NOT idempotent. Every call re-registers the auto-track listeners, starts an ADDITIONAL
// batch timer, and fires a fresh `pageview` — so calling it per event (which is what this helper
// used to do) emitted one phantom pageview per tracked event, and left N flush timers racing each
// other. A page with per-tile impression tracking did that ~30 times in a single load.
//
// app2.blade.php (the Inertia root) only loads the SDK and its queueing stub, so JS genuinely does
// have to init — it just has to do it once.
//
// EXCEPT that app2.blade.php now DOES init, through the queueing stub, so that a direct
// window.LTIAnalytics.track() (CustomTags, CountdownBar, PinnedVideo, useGoldTracking) is not
// replayed ahead of init and refused. It sets window.__ltiInitialised to say so, and this module
// then skips its own init() call.
let initialised = false

function ensureInitialised(props) {
    if (initialised) {
        return
    }

    initialised = true

    if (! window.__ltiInitialised) {
        LTIAnalytics.init(props.lti_analytics.api_key, {
            endpoint: props.lti_analytics.api_url + '/api/v1',
            debug: true,
            autoTrack: true
        });

        window.__ltiInitialised = true
    }

    initPageviewTracking()
}

let pageviewTrackingStarted = false

// init() fires the first pageview itself, and it only runs once per full page load — so an Inertia
// visit, which swaps the component without reloading the document, would otherwise never be
// counted. This sends one pageview per navigation.
//
// Called from app.js at boot. It used to be registered only by ensureInitialised(), i.e. on the
// first trackEvent() — and no members page calls trackEvent(), so on the members SPA it only got
// registered as a side effect of the Web Vitals reporter (production builds only, and only once
// the first vital reported, so a click before that lost its pageview).
//
// Keyed on the URL rather than on the event alone, so this cannot double-count: Inertia fires
// `navigate` for the initial page too (and again for a router.reload() of the same URL), and
// init() has already counted whatever we are on right now.
export function initPageviewTracking() {
    if (pageviewTrackingStarted || typeof window === 'undefined') {
        return
    }

    pageviewTrackingStarted = true

    let lastPageviewUrl = window.location.href

    router.on('navigate', () => {
        if (window.location.href === lastPageviewUrl) {
            return
        }

        lastPageviewUrl = window.location.href

        // Nothing has initialised the SDK yet: roots other than app2.blade.php init lazily, on the
        // first trackEvent(), and that init() counts the page it runs on.
        if (! window.__ltiInitialised || typeof LTIAnalytics === 'undefined') {
            return
        }

        // pageview() is on the real SDK but NOT on the parse-time stub in app2.blade.php, and the
        // SDK script is deferred. track() is stubbed and queues, so it is the safe fallback if a
        // navigation somehow beats the script's load event.
        if (typeof LTIAnalytics.pageview === 'function') {
            LTIAnalytics.pageview();

            return
        }

        LTIAnalytics.track('pageview', {
            title: document.title,
            url: window.location.href,
            path: window.location.pathname,
            referrer: document.referrer,
        });
    })
}

export function trackEvent(event, data) {
    if (typeof LTIAnalytics !== 'undefined') {
        const {props} = usePage()

        ensureInitialised(props)

        // String(), not the raw id. The identify endpoint validates `user_id` as
        // `required|string` (ancms TrackingController::identify) and the JS SDK forwards the value
        // it is handed without coercing it, so an Inertia-serialised numeric id is a JSON number
        // on the wire and comes back 400 "Invalid request" every single time.
        //
        // This is why the Blade path never broke: lti_analytics_init_js() interpolates the id into
        // a quoted JS literal, so it was always a string there.
        //
        // The failure is self-amplifying, which is why it shows up as a wall of 400s rather than
        // one: the SDK only marks the session identified after a SUCCESSFUL identify, so while
        // this is failing every subsequent tracked event retries it.
        const userId = props?.auth?.user?.id

        if (userId !== undefined && userId !== null && identifiedUserId !== String(userId)) {
            identifiedUserId = String(userId)

            LTIAnalytics.identify(String(userId), {
                user_id: String(userId)
            });
        }

        if (data) {
            LTIAnalytics.track(event, data);
        } else {
            LTIAnalytics.track(event);
        }

        console.log(`++ LTI Analytics: Tracked ${event}`);
    } else {
        console.error(`++ LTI Analytics: Failed to track event ${event}. LTIAnalytics is not defined`);
        notifyBugsnag(new Error('LTI Analytics is not defined'), function (bugsnagEvent) {
            bugsnagEvent.severity = 'warning';
            if (data) {
                bugsnagEvent.addMetadata('eventData', data);
            }
        })
    }
}

let lastBounceEventKey = '';
let lastBounceEventAt = 0;

export function trackStarShopBounceEvent(event, data = {}) {
    const bounceKey = `${event}|${data.source ?? ''}|${data.product_id ?? ''}`;
    if (bounceKey === lastBounceEventKey && Date.now() - lastBounceEventAt < 1000) return;
    lastBounceEventKey = bounceKey;
    lastBounceEventAt = Date.now();
    fetch(route('members2.shop.bounce-event'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
        },
        body: JSON.stringify({event, ...data}),
    }).then(res => {
        if (!res.ok) { res.json().then(body => console.warn('Bounce event rejected:', event, body)).catch(() => {}); }
    }).catch(() => {});
}

export function getFathomPostPrefix(routePrefix) {
    return routePrefix === 'members2' ? 'Member_Post' : 'Tour_Post'
}

/**
 * Not reported to Bugsnag when Fathom is missing (NEX-9408): app2.blade always includes
 * the script, so a missing global means a blocker removed it (148 events / 34 users in
 * 3 days, half of them Firefox Mobile) — nothing we can fix, and LTI still records the event.
 */
export function trackFathomEvent(event) {
    if (typeof fathom !== 'undefined' ) {
        fathom.trackEvent(event);
    } else {
        console.error(`++ Fathom: Failed to track event ${event}. Fathom is not defined`);
    }
}

export function getFathomPagePrefix(routePrefix) {
    return routePrefix === 'members2' ? 'Member' : 'Tour';
}

export function trackFeatureEvent(event, featureSlug) {
    if (typeof nanalytics !== 'undefined') {
        nanalytics.trackExperiment(event, featureSlug);
    }
}
